import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignsAPI, leadsAPI, templatesAPI } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import SearchInput from '../components/SearchInput';
import { TableSkeleton } from '../components/LoadingSpinner';
import { useToast } from '../hooks/useToast';
import { CampaignsIcon } from '../components/Icons';
import './Campaigns.css';

export default function Campaigns() {
  const navigate = useNavigate();
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    goal: '',
    timezone: '',
    message_template: '',
    use_personal_messages: false,
    estimated_cost: 0
  });
  const [formErrors, setFormErrors] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  
  // Lead management modal state
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadStatusFilter, setLeadStatusFilter] = useState('');
  const [excludeOtherCampaigns, setExcludeOtherCampaigns] = useState(true);
  const [leadCampaignMap, setLeadCampaignMap] = useState({}); // Map of lead_id -> [campaign names]

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = campaigns.filter(campaign =>
        campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        campaign.goal?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCampaigns(filtered);
    } else {
      setFilteredCampaigns(campaigns);
    }
  }, [searchQuery, campaigns]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await campaignsAPI.getAll();
      // Handle paginated response
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setCampaigns(data);
      setFilteredCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      if (error.response?.status === 403) {
        toast.warning('Authentication required. Please login.');
      } else {
        toast.error('Failed to load campaigns. Check console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await templatesAPI.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Don't show error toast for templates - it's not critical for viewing campaigns
    } finally {
      setTemplatesLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Campaign name is required';
    }
    if (!formData.message_template) {
      errors.message_template = 'Message template is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix form errors');
      return;
    }

    try {
      setActionLoading('save');
      const data = {
        name: formData.name.trim(),
        goal: formData.goal.trim() || '',
        timezone: formData.timezone.trim() || '',
        message_template: formData.message_template,
        use_personal_messages: formData.use_personal_messages,
        estimated_cost: parseFloat(formData.estimated_cost) || 0,
        active: false // Safety: New campaigns start inactive
      };

      if (editingCampaign) {
        await campaignsAPI.update(editingCampaign.id, data);
        toast.success('Campaign updated successfully');
      } else {
        await campaignsAPI.create(data);
        toast.success('Campaign created successfully');
      }
      
      setShowModal(false);
      setEditingCampaign(null);
      setFormData({ name: '', goal: '', timezone: '', message_template: '', use_personal_messages: false, estimated_cost: 0 });
      setFormErrors({});
      loadCampaigns();
    } catch (error) {
      console.error('Failed to save campaign:', error);
      const errorMsg = error.response?.data?.name?.[0] || 
                      error.response?.data?.detail || 
                      'Failed to save campaign. Check console for details.';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleNew = () => {
    setEditingCampaign(null);
    setFormData({ name: '', goal: '', timezone: '', message_template: '', use_personal_messages: false, estimated_cost: 0 });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name || '',
      goal: campaign.goal || '',
      timezone: campaign.timezone || '',
      message_template: campaign.message_template || '',
      use_personal_messages: campaign.use_personal_messages || false,
      estimated_cost: campaign.estimated_cost || 0
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingCampaign(null);
    setFormData({ name: '', goal: '', timezone: '', message_template: '', use_personal_messages: false, estimated_cost: 0 });
    setFormErrors({});
  };

  const toggleActive = async (id) => {
    const campaign = campaigns.find(c => c.id === id);
    const action = campaign?.active ? 'pause' : 'activate';
    
    if (!window.confirm(`Are you sure you want to ${action} this campaign?`)) {
      return;
    }

    try {
      setActionLoading(id);
      await campaignsAPI.toggleActive(id);
      toast.success(`Campaign ${action === 'pause' ? 'paused' : 'activated'} successfully`);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to toggle campaign:', error);
      toast.error('Failed to toggle campaign. Check console for details.');
    } finally {
      setActionLoading(null);
    }
  };

  const loadLeadData = async (campaign) => {
    if (!campaign) return;
    
    setLeadLoading(true);
    
    try {
      // Fetch all data in parallel for better performance
      const [leadsResponse, assignmentsResponse, campaignLeadsResponse] = await Promise.all([
        leadsAPI.getAll(),
        campaignsAPI.getLeadAssignments(),
        campaignsAPI.getLeads(campaign.id)
      ]);
      
      // Process leads
      const allLeads = Array.isArray(leadsResponse.data) 
        ? leadsResponse.data 
        : (leadsResponse.data?.results || leadsResponse.data?.leads || []);
      setAvailableLeads(allLeads);
      
      // Process lead assignments (exclude current campaign)
      const assignments = assignmentsResponse.data?.lead_assignments || {};
      const leadToCampaigns = {};
      
      for (const [leadId, campaigns] of Object.entries(assignments)) {
        // Filter out current campaign and only show campaign names
        const otherCampaigns = campaigns
          .filter(c => c.campaign_id !== campaign.id)
          .map(c => c.campaign_name);
        
        if (otherCampaigns.length > 0) {
          leadToCampaigns[leadId] = otherCampaigns;
        }
      }
      setLeadCampaignMap(leadToCampaigns);
      
      // Process current campaign leads
      const campaignLeads = campaignLeadsResponse.data?.leads || [];
      const campaignLeadIds = campaignLeads.map(l => l.id);
      setSelectedLeadIds(campaignLeadIds);
      
      toast.success('Leads data refreshed');
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error('Failed to refresh leads. Check console for details.');
    } finally {
      setLeadLoading(false);
    }
  };

  const handleManageLeads = async (campaign) => {
    setSelectedCampaign(campaign);
    setShowLeadModal(true);
    setLeadStatusFilter('');
    setExcludeOtherCampaigns(true);
    await loadLeadData(campaign);
  };

  const handleRefreshLeads = async () => {
    if (selectedCampaign) {
      await loadLeadData(selectedCampaign);
    }
  };

  const handleToggleLead = (leadId) => {
    setSelectedLeadIds(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredLeads();
    if (filtered.every(lead => selectedLeadIds.includes(lead.id))) {
      // Deselect all filtered leads
      setSelectedLeadIds(prev => prev.filter(id => !filtered.map(l => l.id).includes(id)));
    } else {
      // Select all filtered leads
      const newIds = filtered.map(l => l.id);
      setSelectedLeadIds(prev => [...new Set([...prev, ...newIds])]);
    }
  };

  const getFilteredLeads = () => {
    let filtered = [...availableLeads];
    
    // Filter by status
    if (leadStatusFilter) {
      filtered = filtered.filter(lead => lead.status === leadStatusFilter);
    }
    
    // Filter by search query
    if (leadSearchQuery) {
      const query = leadSearchQuery.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.email?.toLowerCase().includes(query) ||
        lead.first_name?.toLowerCase().includes(query) ||
        lead.last_name?.toLowerCase().includes(query)
      );
    }
    
    // Exclude leads already in other campaigns
    if (excludeOtherCampaigns) {
      filtered = filtered.filter(lead => !leadCampaignMap[lead.id] || leadCampaignMap[lead.id].length === 0);
    }
    
    return filtered;
  };
  
  const getLeadCampaigns = (leadId) => {
    return leadCampaignMap[leadId] || [];
  };

  const handleSaveLeads = async () => {
    if (!selectedCampaign) return;
    
    try {
      setActionLoading('save_leads');
      
      // Get current leads in campaign
      const currentResponse = await campaignsAPI.getLeads(selectedCampaign.id);
      const currentLeads = currentResponse.data?.leads || [];
      const currentLeadIds = currentLeads.map(l => l.id);
      
      // Calculate adds and removes
      const toAdd = selectedLeadIds.filter(id => !currentLeadIds.includes(id));
      const toRemove = currentLeadIds.filter(id => !selectedLeadIds.includes(id));
      
      if (toAdd.length > 0) {
        await campaignsAPI.assignLeads(selectedCampaign.id, { lead_ids: toAdd });
      }
      
      if (toRemove.length > 0) {
        await campaignsAPI.removeLeads(selectedCampaign.id, { lead_ids: toRemove });
      }
      
      toast.success(`Updated leads for ${selectedCampaign.name}`);
      setShowLeadModal(false);
      loadCampaigns();
    } catch (error) {
      console.error('Error updating leads:', error);
      toast.error('Failed to update leads. Check console for details.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseLeadModal = () => {
    setShowLeadModal(false);
    setSelectedCampaign(null);
    setAvailableLeads([]);
    setSelectedLeadIds([]);
    setLeadSearchQuery('');
    setLeadStatusFilter('');
    setExcludeOtherCampaigns(true);
    setLeadCampaignMap({});
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Campaigns</h1>
        </div>
        <Card>
          <TableSkeleton rows={5} cols={5} />
        </Card>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Campaigns</h1>
          <p className="page-subtitle">Manage your outreach campaigns</p>
        </div>
        <div className="page-actions">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search campaigns..."
            style={{ maxWidth: '300px' }}
          />
          <Button variant="primary" onClick={handleNew}>
            + Create Campaign
          </Button>
        </div>
      </div>

      {filteredCampaigns.length === 0 ? (
        <Card>
          <div className="empty-state">
            <div className="empty-state-icon">
              <CampaignsIcon size={48} style={{ opacity: 0.5 }} />
            </div>
            <div className="empty-state-title">
              {searchQuery ? 'No campaigns found' : 'No campaigns yet'}
            </div>
            <div className="empty-state-description">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Create your first campaign to get started with automated outreach'}
            </div>
            {!searchQuery && (
              <Button variant="primary" onClick={handleNew} style={{ marginTop: 'var(--spacing-lg)' }}>
                Create First Campaign
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Goal</th>
                  <th>Steps</th>
                  <th>Leads</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map(campaign => (
                  <tr key={campaign.id}>
                    <td>
                      <div className="campaign-name">
                        <strong>{campaign.name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="text-secondary">{campaign.goal || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="badge">{campaign.steps_count || 0}</span>
                    </td>
                    <td>
                      <span className="badge">{campaign.lead_count || 0}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${campaign.active ? 'status-active' : 'status-inactive'}`}>
                        {campaign.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span className="text-secondary">
                        {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Button
                          variant={campaign.active ? 'warning' : 'success'}
                          size="sm"
                          onClick={() => toggleActive(campaign.id)}
                          loading={actionLoading === campaign.id}
                        >
                          {campaign.active ? 'Pause' : 'Activate'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/campaigns/${campaign.id}/steps`)}
                        >
                          Manage Steps
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleManageLeads(campaign)}
                        >
                          Manage Leads ({campaign.lead_count || 0})
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(campaign)}
                        >
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal 
        show={showModal} 
        onClose={handleClose} 
        title={editingCampaign ? 'Edit Campaign' : 'Create Campaign'} 
        width="600px"
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Campaign Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            error={formErrors.name}
            placeholder="e.g., Q1 Email Outreach"
            autoFocus
          />

          <Input
            label="Goal (Optional)"
            type="textarea"
            value={formData.goal}
            onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
            placeholder="Describe the purpose of this campaign..."
            rows={4}
          />

          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--font-size-sm)', 
              fontWeight: 'var(--font-weight-semibold)', 
              color: 'var(--text-primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              Message Template <span style={{ color: 'var(--error)' }}>*</span>
            </label>
            {templatesLoading ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>Loading templates...</p>
            ) : templates.length === 0 ? (
              <div style={{ 
                padding: 'var(--spacing-sm)', 
                background: 'rgba(251, 191, 36, 0.1)', 
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: 'var(--border-radius)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-secondary)'
              }}>
                No templates available. <a href="/templates" style={{ color: 'var(--primary)' }}>Create a template first</a>.
              </div>
            ) : (
              <select
                value={formData.message_template}
                onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-family)',
                  color: 'var(--text-primary)',
                  background: 'var(--bg-primary)',
                  border: formErrors.message_template ? '1px solid var(--error)' : '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius)',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select a template...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.subject || 'No Subject'}
                  </option>
                ))}
              </select>
            )}
            {formErrors.message_template && (
              <p style={{ color: 'var(--error)', fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)', marginBottom: 0 }}>
                {formErrors.message_template}
              </p>
            )}
            <p style={{ 
              fontSize: 'var(--font-size-xs)', 
              color: 'var(--text-secondary)',
              marginTop: 'var(--spacing-xs)',
              marginBottom: 0
            }}>
              Default message template for this campaign. Required for all campaigns.
            </p>
          </div>

          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 'var(--spacing-sm)',
              fontSize: 'var(--font-size-sm)', 
              fontWeight: 'var(--font-weight-semibold)', 
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.use_personal_messages}
                onChange={(e) => setFormData({ ...formData, use_personal_messages: e.target.checked })}
                style={{ cursor: 'pointer' }}
              />
              Use Personal Messages
            </label>
            <p style={{ 
              fontSize: 'var(--font-size-xs)', 
              color: 'var(--text-secondary)',
              marginTop: 'var(--spacing-xs)',
              marginLeft: '24px',
              marginBottom: 0
            }}>
              If enabled, use personal messages per lead (stored in lead's personal_messages field). 
              If a lead doesn't have a personal message for a step, the campaign template will be used as fallback.
            </p>
          </div>

          <Input
            label="Estimated Cost (Optional)"
            type="number"
            value={formData.estimated_cost}
            onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
            placeholder="0.00"
            min="0"
            step="0.01"
            helperText="Estimated cost to run this campaign (for ROI calculation)"
          />

          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--font-size-sm)', 
              fontWeight: 'var(--font-weight-semibold)', 
              color: 'var(--text-primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              Timezone (Optional)
            </label>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              style={{
                width: '100%',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                fontSize: 'var(--font-size-base)',
                fontFamily: 'var(--font-family)',
                color: 'var(--text-primary)',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--border-radius)',
                cursor: 'pointer'
              }}
            >
              <option value="">Use lead timezone (default)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time (America/New_York)</option>
              <option value="America/Chicago">Central Time (America/Chicago)</option>
              <option value="America/Denver">Mountain Time (America/Denver)</option>
              <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
              <option value="Europe/London">London (Europe/London)</option>
              <option value="Europe/Paris">Paris (Europe/Paris)</option>
              <option value="Europe/Berlin">Berlin (Europe/Berlin)</option>
              <option value="Asia/Dubai">Dubai (Asia/Dubai)</option>
              <option value="Asia/Karachi">Karachi (Asia/Karachi)</option>
              <option value="Asia/Dhaka">Dhaka (Asia/Dhaka)</option>
              <option value="Asia/Kolkata">Mumbai/Delhi (Asia/Kolkata)</option>
              <option value="Asia/Singapore">Singapore (Asia/Singapore)</option>
              <option value="Asia/Tokyo">Tokyo (Asia/Tokyo)</option>
              <option value="Australia/Sydney">Sydney (Australia/Sydney)</option>
            </select>
            <p style={{ 
              fontSize: 'var(--font-size-xs)', 
              color: 'var(--text-secondary)',
              marginTop: 'var(--spacing-xs)',
              marginBottom: 0
            }}>
              Campaign send times will use this timezone. If not set, uses each lead's timezone.
            </p>
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 'var(--spacing-md)', 
            marginTop: 'var(--spacing-xl)' 
          }}>
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={actionLoading === 'save'}>
              {editingCampaign ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Lead Management Modal */}
      <Modal
        show={showLeadModal}
        onClose={handleCloseLeadModal}
        title={`Manage Leads - ${selectedCampaign?.name || ''}`}
        width="95vw"
        maxWidth="1400px"
      >
        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Select leads to assign to this campaign. Selected: {selectedLeadIds.length} / {getFilteredLeads().length} shown ({availableLeads.length} total)
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefreshLeads}
                loading={leadLoading}
                title="Refresh lead data"
              >
                ↻ Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                {getFilteredLeads().every(lead => selectedLeadIds.includes(lead.id)) ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <SearchInput
                value={leadSearchQuery}
                onChange={(e) => setLeadSearchQuery(e.target.value)}
                placeholder="Search leads by name or email..."
              />
            </div>
            
            <div style={{ minWidth: '150px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: 'var(--font-size-sm)', 
                fontWeight: 'var(--font-weight-semibold)', 
                color: 'var(--text-primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Status Filter
              </label>
              <select
                value={leadStatusFilter}
                onChange={(e) => setLeadStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-family)',
                  color: 'var(--text-primary)',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius)',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Statuses</option>
                <option value="NEW">New</option>
                <option value="CONTACTED">Contacted</option>
                <option value="ENGAGED">Engaged</option>
                <option value="REPLIED">Replied</option>
                <option value="BOOKED">Booked</option>
                <option value="NOT_INTERESTED">Not Interested</option>
                <option value="DEAD">Dead</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <input
                type="checkbox"
                id="exclude-other-campaigns"
                checked={excludeOtherCampaigns}
                onChange={(e) => setExcludeOtherCampaigns(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <label 
                htmlFor="exclude-other-campaigns"
                style={{ 
                  fontSize: 'var(--font-size-sm)', 
                  color: 'var(--text-primary)',
                  cursor: 'pointer'
                }}
              >
                Exclude leads in other campaigns
              </label>
            </div>
          </div>
          
          {!excludeOtherCampaigns && Object.keys(leadCampaignMap).length > 0 && (
            <div style={{ 
              marginTop: 'var(--spacing-sm)', 
              padding: 'var(--spacing-sm)', 
              background: 'rgba(251, 191, 36, 0.1)', 
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: 'var(--border-radius)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-secondary)'
            }}>
              <strong>Note:</strong> Some leads are already assigned to other campaigns. They will be marked with a warning icon.
            </div>
          )}
        </div>

        {leadLoading ? (
          <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
            <p>Loading leads...</p>
          </div>
        ) : availableLeads.length === 0 ? (
          <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
            <p>No leads available. Create some leads first.</p>
          </div>
        ) : (
          <div style={{ 
            maxHeight: '70vh', 
            minHeight: '500px',
            overflowY: 'auto', 
            border: '1px solid var(--border-color)', 
            borderRadius: 'var(--border-radius)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <table className="data-table" style={{ marginBottom: 0, width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--background-primary)', zIndex: 1 }}>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>
                    <input 
                      type="checkbox" 
                      checked={getFilteredLeads().length > 0 && getFilteredLeads().every(lead => selectedLeadIds.includes(lead.id))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ width: '20%', textAlign: 'left' }}>Name</th>
                  <th style={{ width: '25%', textAlign: 'left' }}>Email</th>
                  <th style={{ width: '15%', textAlign: 'left' }}>Status</th>
                  <th style={{ width: '40%', textAlign: 'left' }}>Other Campaigns</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredLeads().map(lead => {
                  const otherCampaigns = getLeadCampaigns(lead.id);
                  return (
                    <tr key={lead.id}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={() => handleToggleLead(lead.id)}
                        />
                      </td>
                      <td style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {lead.first_name} {lead.last_name}
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>{lead.email}</td>
                      <td>
                        <span className="badge">{lead.status}</span>
                      </td>
                      <td style={{ fontSize: 'var(--font-size-sm)' }}>
                        {otherCampaigns.length > 0 ? (
                          <span 
                            style={{ 
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--warning)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title={`Already in: ${otherCampaigns.join(', ')}`}
                          >
                            ⚠ {otherCampaigns.length} campaign{otherCampaigns.length > 1 ? 's' : ''}
                            <span style={{ color: 'var(--text-secondary)', marginLeft: '4px' }}>
                              ({otherCampaigns.join(', ')})
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)' }}>
                            None
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 'var(--spacing-md)', 
          marginTop: 'var(--spacing-lg)' 
        }}>
          <Button type="button" variant="ghost" onClick={handleCloseLeadModal}>
            Cancel
          </Button>
          <Button 
            type="button" 
            variant="primary" 
            onClick={handleSaveLeads}
            loading={actionLoading === 'save_leads'}
          >
            Save Changes
          </Button>
        </div>
      </Modal>
    </div>
  );
}
